// sportsSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../api/client';
import { performGlobalLogout } from './authSlice';

// ============================================================
// FETCH SPORTS LISTING
// ============================================================
export const fetchSportsListing = createAsyncThunk(
  'sports/listing',
  async (etid, { rejectWithValue }) => {
    try {
      const response = await api.get('/sports/listing', { etid });
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: 'Failed to load sports listing',
        }
      );
    }
  }
);

// ============================================================
// MATCH TIMING
// ============================================================
export const matchTiming = createAsyncThunk(
  'sports/matchTiming',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/bets/match/dates');
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: 'Failed to load match timing',
        }
      );
    }
  }
);

// ============================================================
// GET DELAY LOADER
// ============================================================
export const delayloader = createAsyncThunk(
  'sports/delayloader',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/auth/delay/${userId}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: 'Failed to load match timing',
        }
      );
    }
  }
);

// ============================================================
// GET EDIT STAKE
// ============================================================
export const GetEditStake = createAsyncThunk(
  'sports/GetEditStake',
  async ({ userId, GAME }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/casino-games/user/${userId}/${GAME}`
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: 'Failed to load match timing',
        }
      );
    }
  }
);

// ============================================================
// UPDATE EDIT STAKE
// ============================================================
export const UpdateEditStake = createAsyncThunk(
  'sports/UpdateEditStake',
  async (
    {
      userId,
      GAME,
      globalLabel,
      newLabel,
      newValue,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(
        `/casino-games/user/${userId}/${GAME}`,
        {
          globalLabel,
          newLabel,
          newValue,
        }
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: 'Failed to update stake',
        }
      );
    }
  }
);

// ============================================================
// GET SPORT TOGGLE
// ============================================================
export const getSportToggle = createAsyncThunk(
  'sports/getSportToggle',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/sports/sports');

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: 'Failed to load sport toggle',
        }
      );
    }
  }
);

// ============================================================
// INITIAL STATE
// ============================================================
const initialState = {
  data: [],
  matchTimingData: [],
  delayloaderData: [],
  GetEditStakeData: [],
  getSportToggleData: [],
  UpdateEditStake: null,

  loading: false,
  loadingListing: false,
  error: null,
};

// ============================================================
// SPORTS SLICE
// ============================================================
const sportsSlice = createSlice({
  name: 'sports',

  initialState,

  reducers: {},

  extraReducers: (builder) => {
    builder

      // ========================================================
      // SPORTS LISTING
      // ========================================================
      .addCase(fetchSportsListing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchSportsListing.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload?.data || [];
      })

      .addCase(fetchSportsListing.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message ||
          action.error?.message ||
          'Something went wrong';
      })

      // ========================================================
      // MATCH TIMING
      // ========================================================
      .addCase(matchTiming.pending, (state) => {
        state.loadingListing = true;
        state.error = null;
      })

      .addCase(matchTiming.fulfilled, (state, action) => {
        state.loadingListing = false;
        state.matchTimingData = action.payload || [];
      })

      .addCase(matchTiming.rejected, (state, action) => {
        state.loadingListing = false;
        state.error =
          action.payload?.message ||
          action.error?.message ||
          'Something went wrong';
      })

      // ========================================================
      // DELAY LOADER
      // ========================================================
      .addCase(delayloader.pending, (state) => {
        state.loadingListing = true;
        state.error = null;
      })

      .addCase(delayloader.fulfilled, (state, action) => {
        state.loadingListing = false;
        state.delayloaderData = action.payload || [];
      })

      .addCase(delayloader.rejected, (state, action) => {
        state.loadingListing = false;
        state.error =
          action.payload?.message ||
          action.error?.message ||
          'Something went wrong';
      })

      // ========================================================
      // GET EDIT STAKE
      // ========================================================
      .addCase(GetEditStake.pending, (state) => {
        state.loadingListing = true;
        state.error = null;
      })

      .addCase(GetEditStake.fulfilled, (state, action) => {
        state.loadingListing = false;

        const newData =
          action.payload?.data ||
          action.payload?.result ||
          action.payload ||
          [];

        if (Array.isArray(newData) && newData.length > 0) {
          const incomingType = newData[0]?.type;

          const existing = (
            state.GetEditStakeData || []
          ).filter(
            (item) => item.type !== incomingType
          );

          state.GetEditStakeData = [
            ...existing,
            ...newData,
          ];
        }
      })

      .addCase(GetEditStake.rejected, (state, action) => {
        state.loadingListing = false;
        state.error =
          action.payload?.message ||
          action.error?.message ||
          'Something went wrong';
      })

      // ========================================================
      // UPDATE EDIT STAKE
      // ========================================================
      .addCase(UpdateEditStake.pending, (state) => {
        state.loadingListing = true;
        state.error = null;
      })

      .addCase(UpdateEditStake.fulfilled, (state, action) => {
        state.loadingListing = false;
        state.UpdateEditStake = action.payload || null;
      })

      .addCase(UpdateEditStake.rejected, (state, action) => {
        state.loadingListing = false;
        state.UpdateEditStake = null;
        state.error =
          action.payload?.message ||
          action.error?.message ||
          'Something went wrong';
      })

      // ========================================================
      // SPORT TOGGLE
      // ========================================================
      .addCase(getSportToggle.pending, (state) => {
        state.loadingListing = true;
        state.error = null;
      })

      .addCase(getSportToggle.fulfilled, (state, action) => {
        state.loadingListing = false;

        state.getSportToggleData =
          action.payload?.data ||
          action.payload ||
          [];
      })

      .addCase(getSportToggle.rejected, (state, action) => {
        state.loadingListing = false;
        state.error =
          action.payload?.message ||
          action.error?.message ||
          'Something went wrong';
      })

      // ========================================================
      // GLOBAL LOGOUT
      // ========================================================
      .addCase(
        performGlobalLogout.fulfilled,
        (state) => {
          state.data = [];
          state.matchTimingData = [];
          state.delayloaderData = [];
          state.GetEditStakeData = [];
          state.UpdateEditStake = null;
          state.getSportToggleData = [];

          state.loading = false;
          state.loadingListing = false;
          state.error = null;
        }
      );
  },
});

// ============================================================
// EXPORT REDUCER
// ============================================================
export default sportsSlice.reducer;